import { memo, useState, useRef, useCallback, useEffect } from 'react';
import classNames from 'classnames';

import { useOnClickOutside } from 'utils';

import { ReactComponent as ArrowDownIcon } from 'assets/images/arrow-down.svg';

import { DropdownItem, DropdownOption } from './components/DropdownItem';

import styles from './styles.module.scss';

type Props = {
  name: string;
  selectedOption: DropdownOption;
  options: DropdownOption[];
  label?: string;
  className?: string;
  error?: string;
  onChange: (selectedOption: DropdownOption) => void;
  onBlur?: () => void;
};

export const Dropdown: React.FC<Props> = memo(function Dropdown({
  name,
  selectedOption,
  options,
  label,
  className,
  error,
  onChange,
  onBlur,
}) {
  const [dropdownOpened, setDropdownOpened] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(
    dropdownRef,
    useCallback(() => setDropdownOpened(false), []),
  );
  const isMounted = useRef(false);

  useEffect(() => {
    if (isMounted.current) {
      if (!dropdownOpened && onBlur) {
        onBlur();
      }
    } else {
      isMounted.current = true;
    }
  }, [dropdownOpened, onBlur]);

  const handleSelectedItemClick = useCallback(() => {
    setDropdownOpened(true);
  }, []);

  const handleSelectedOptionChange = useCallback(
    (selectedOption: DropdownOption) => {
      onChange(selectedOption);
      setDropdownOpened(false);
    },
    [onChange],
  );

  return (
    <div className={classNames(styles.container, className, { [styles.error_container]: error })}>
      {label && <label htmlFor={name}>{label}</label>}
      <div
        onClick={handleSelectedItemClick}
        className={classNames({ [styles.active_item]: dropdownOpened })}>
        <p>{selectedOption.label}</p>
        <ArrowDownIcon className={classNames({ [styles.rotated_icon]: dropdownOpened })} />
      </div>
      {error && <p>{error}</p>}
      {dropdownOpened && (
        <div ref={dropdownRef}>
          {options.map((option) => (
            <DropdownItem key={option.value} option={option} onClick={handleSelectedOptionChange} />
          ))}
        </div>
      )}
    </div>
  );
});
